// The point stars: real catalogue stars drawn the way one camera records
// them, on the sky map's background (main.js createStarfield).
//
//   - /data/stars.bin: HYG v4.2 down to magnitude 8 (~41k, scripts/build-stars.js),
//     loaded at once.
//   - /data/stars-faint.bin.gz: Tycho-2 from 8 to 10 (scripts/build-tycho-stars.mjs),
//     loaded after the page is up. Fainter stars are in the background map.
//
// Every star has the same point-spread function (a Gaussian core and a
// Moffat wing, as a lens and the eye give); only its flux, from the
// magnitude, changes. A bright star looks bigger because more of its wing
// clears the dark, as in a photograph, and its core clips to white where
// the channels saturate. One exposure sets what is visible: stopping down
// for a sunlit planet or the Sun in frame loses the faint stars first.
// Stars only twinkle through air (setScintillation), never in space.
//
// Positions are J2000 equatorial carried into the scene by src/sky/frame.js.
import * as THREE from 'three';
import { equatorialToScene } from './sky/frame.js';
import { gunzipIfNeeded } from './gunzip.js';

// Kept here for the modules that import it from this file.
export { gunzipIfNeeded };

const STAR_BIN_URL = '/data/stars.bin';
const FAINT_BIN_URL = '/data/stars-faint.bin.gz';

// Place stars just inside the sky sphere (40M in main.js) so camera moves
// never move them against the map.
const STAR_SHELL_RADIUS = 39_500_000;

// Flux 1 is a magnitude 6 star (about the naked-eye limit under a dark sky).
export const STAR_REFERENCE_MAG = 6;
const fluxOf = (mag) => Math.pow(10, -0.4 * (mag - STAR_REFERENCE_MAG));

// Stellar colour via the physically-grounded Planck pipeline. Replaces
// the previous 8-anchor OKLCh-interpolated lookup with the continuous
// equation chain that astrophysics texts and real astrophotography
// post-processing use:
//
//   B-V  →  T (Kelvin)     Ballesteros 2012 inversion
//   T    →  B_λ(T) spectrum  Planck 1900 black-body radiance law
//   B_λ  →  XYZ tristimulus  ∫ B_λ · (x̄, ȳ, z̄)(λ) dλ  using CIE 1931
//                            colour matching functions (Wyman 2013
//                            piecewise-gaussian analytic fits)
//   XYZ  →  linear sRGB     standard D65 transform, clipped to gamut
//
// Y-normalised to constant luminance, then peak-renormalised so the
// chromaticity is preserved while the brightest channel sits at 1.0
// (apparent brightness comes from the magnitude → point size scale
// in the catalog loader, not from chromaticity).
//
// References:
//   - Ballesteros 2012, "New insights into black bodies" (EPL 97 34008)
//   - Wyman / Sloan / Shirley 2013, "Simple Analytic Approximations
//     to the CIE XYZ Color Matching Functions" (JCGT 2:2)
//   - CIE 1931 standard observer; sRGB D65 transform per IEC 61966-2-1

const H_PLANCK = 6.62607015e-34;
const C_LIGHT  = 2.99792458e8;
const K_BOLTZ  = 1.380649e-23;

// Ballesteros 2012 closed-form B-V → effective temperature inversion.
// Accurate to better than ~5% across the main-sequence range. Cross-
// checks at canonical anchors: B-V 0.00 → 10125 K (Sirius ≈ 9940 K),
// B-V 0.65 → 5778 K (Sun, exact), B-V 1.85 → 3334 K (Betelgeuse ≈
// 3500 K). The deviation lies inside the spread of catalogue values
// for those same stars across different sources.
function ballesterosBVtoT(bv) {
  return 4600 * (1 / (0.92 * bv + 1.7) + 1 / (0.92 * bv + 0.62));
}

// Planck spectral radiance B_λ(T) per unit wavelength. Constant pre-
// factors cancel in the later luminance normalisation, so the absolute
// units here don't matter — only the relative spectrum across λ does.
function planckRadiance(lambdaNm, T) {
  const lambdaM = lambdaNm * 1e-9;
  const hc_lkT = (H_PLANCK * C_LIGHT) / (lambdaM * K_BOLTZ * T);
  return 1 / (Math.pow(lambdaM, 5) * (Math.exp(hc_lkT) - 1));
}

// Wyman 2013 piecewise-gaussian fits to the CIE 1931 2° standard
// observer x̄/ȳ/z̄. Each fit is two or three asymmetric gaussians; σ
// differs on each side of the peak so the fit captures the real
// asymmetry of the matching functions. Max error vs the tabulated
// CIE data is < 1.5% across 380–780nm, well below the visual
// difference threshold for stellar colour.
function _gaussAsym(lambda, mu, sigmaL, sigmaR) {
  const s = lambda < mu ? sigmaL : sigmaR;
  const x = (lambda - mu) / s;
  return Math.exp(-0.5 * x * x);
}
function cieXBar(lambda) {
  return 0.362 * _gaussAsym(lambda, 442.0, 16.0, 26.7)
       + 1.056 * _gaussAsym(lambda, 599.8, 37.9, 31.0)
       - 0.065 * _gaussAsym(lambda, 501.1, 20.4, 26.2);
}
function cieYBar(lambda) {
  return 0.821 * _gaussAsym(lambda, 568.8, 46.9, 40.5)
       + 0.286 * _gaussAsym(lambda, 530.9, 16.3, 31.1);
}
function cieZBar(lambda) {
  return 1.217 * _gaussAsym(lambda, 437.0, 11.8, 36.0)
       + 0.681 * _gaussAsym(lambda, 459.0, 26.0, 13.8);
}

// Integrate the Planck spectrum against CIE colour matching functions
// to get XYZ, then transform to linear sRGB under D65 illuminant.
// Step size of 5nm across 380–780nm gives 81 samples — accurate to
// better than 0.5% vs a 1nm integration, and fast enough that 40k
// stars at catalogue load completes in well under a second.
function temperatureToLinearSRgb(T) {
  let X = 0, Y = 0, Z = 0;
  const STEP = 5;
  for (let lambda = 380; lambda <= 780; lambda += STEP) {
    const B = planckRadiance(lambda, T);
    X += B * cieXBar(lambda);
    Y += B * cieYBar(lambda);
    Z += B * cieZBar(lambda);
  }
  // Step cancels in the ratio, no need to multiply through.
  // Y-normalise: constant luminance across all temperatures. The
  // chromaticity is what carries the spectral identity; brightness
  // is the catalog's job via magnitude → point size.
  if (Y <= 0) return [1, 1, 1];
  X /= Y; Z /= Y; Y = 1;
  // XYZ (D65) → linear sRGB. IEC 61966-2-1.
  let r = X *  3.2406 + Y * -1.5372 + Z * -0.4986;
  let g = X * -0.9689 + Y *  1.8758 + Z *  0.0415;
  let b = X *  0.0557 + Y * -0.2040 + Z *  1.0570;
  // Out-of-gamut clip. Cool stars (under ~3000K) have negative blue
  // post-transform because their Planck output is well outside sRGB
  // — clamping to zero is the standard "clip to gamut" path; an
  // alternative is the Bradford desaturation but for our use case
  // (stars at infinity, point-sized) the clip is visually fine.
  r = Math.max(0, r);
  g = Math.max(0, g);
  b = Math.max(0, b);
  // Peak-renormalise so the strongest channel sits at 1.0. Preserves
  // the chromaticity ratio while keeping the rendered colour at full
  // intensity (astrophotography colour-calibration does the same to
  // keep e.g. Betelgeuse's deep red from being three stops dimmer
  // than Sirius's blue-white).
  const peak = Math.max(r, g, b);
  if (peak > 0) { r /= peak; g /= peak; b /= peak; }
  return [r, g, b];
}

// Public entry point preserved so the catalog loader doesn't have to
// change. Internally B-V is routed through Ballesteros 2012 → Planck
// → CIE → sRGB instead of the previous 8-anchor table.
function bvToRgb(bv) {
  const c = Math.max(-0.4, Math.min(2.0, bv));
  const T = ballesterosBVtoT(c);
  return temperatureToLinearSRgb(T);
}

// Colour of unit luminance: V magnitude is the star's brightness, so the
// colour may not change it (a red giant's red channel runs above 1).
const LUT_N = 256, BV_MIN = -0.4, BV_MAX = 2.0;
let colourLut = null;
export function unitLuminanceColour(bv, out, o) {
  if (!colourLut) {
    colourLut = new Float32Array(LUT_N * 3);
    for (let k = 0; k < LUT_N; k++) {
      const [r, g, b] = bvToRgb(BV_MIN + (BV_MAX - BV_MIN) * (k / (LUT_N - 1)));
      const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      colourLut[k * 3] = r / y; colourLut[k * 3 + 1] = g / y; colourLut[k * 3 + 2] = b / y;
    }
  }
  const t = (Math.min(BV_MAX, Math.max(BV_MIN, bv)) - BV_MIN) / (BV_MAX - BV_MIN) * (LUT_N - 1);
  const k = Math.min(LUT_N - 2, Math.floor(t)), f = t - k;
  for (let c = 0; c < 3; c++) out[o + c] = colourLut[k * 3 + c] * (1 - f) + colourLut[(k + 1) * 3 + c] * f;
}

// The point-spread function, in CSS pixels: a core of sigma STAR_SIGMA and a
// Moffat wing of HALO_A of the core's peak, radius HALO_R, power HALO_BETA.
// The display curve maps the linear pixel value v (1 = the peak of a
// magnitude 6 star at exposure 1) to output: v^GAMMA, rolling off to CEILING
// so the brightest cores clip into the bloom instead of running away.
const STAR_SIGMA = 0.5;
const HALO_A = 0.05;
const HALO_R = 1.6;
const HALO_BETA = 1.8;
const GAMMA = 0.6;
const CEILING = 7.0;
// Output below this is not drawn: it sets each sprite's size.
const VISIBLE = 0.004;
// The PSF's integral in CSS pixels (peak 1): how much pixel value one unit
// of flux spreads over. The sky map uses it to put its light on the same
// scale (main.js createStarfield).
export const STAR_PSF_AREA = 2 * Math.PI * STAR_SIGMA * STAR_SIGMA + HALO_A * Math.PI * HALO_R * HALO_R / (HALO_BETA - 1);

// The point-spread function's reach, shared with the asteroids
// (src/sky/asteroids.js), which compute their own peak: where the output
// drops to VISIBLE, the core's and the wing's.
export const STAR_REACH_GLSL = /* glsl */`
  const float SIGMA = ${STAR_SIGMA.toFixed(3)};
  const float HALO_A = ${HALO_A.toFixed(4)};
  const float HALO_R = ${HALO_R.toFixed(3)};
  const float HALO_BETA = ${HALO_BETA.toFixed(3)};
  const float GAMMA = ${GAMMA.toFixed(3)};
  const float VISIBLE = ${VISIBLE.toFixed(4)};
  float starReach(float peak) {
    float floorV = pow(VISIBLE, 1.0 / GAMMA);
    float rCore = peak > floorV ? SIGMA * sqrt(2.0 * log(peak / floorV)) : 0.0;
    float rHalo = HALO_A * peak > floorV ? HALO_R * sqrt(pow(HALO_A * peak / floorV, 1.0 / HALO_BETA) - 1.0) : 0.0;
    return max(rCore, rHalo);
  }
`;

const STAR_VERTEX = /* glsl */`
  attribute vec3 color;
  attribute float flux;
  uniform float pixelRatio;
  uniform float exposure;
  uniform float uScintillation;
  uniform float uTime;
  varying vec3 vColor;
  varying float vPeak;
  varying float vHalfSize;
  ${STAR_REACH_GLSL}
  void main() {
    vColor = color;
    float peak = flux * exposure;
    // Scintillation through air only: two out-of-phase sines per star, the
    // phase hashed from its direction.
    if (uScintillation > 0.0) {
      vec3 d = normalize(position);
      float ph = fract(sin(dot(d, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
      peak *= 1.0 + uScintillation * 0.45 * (0.62 * sin(uTime * 9.1 + ph * 6.2832) + 0.38 * sin(uTime * 17.3 + ph * 19.7));
    }
    vPeak = peak;
    float r = starReach(peak);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    if (r <= 0.0) {
      // Too faint at this exposure: off screen, no fragments.
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      gl_PointSize = 0.0;
      vHalfSize = 0.0;
      return;
    }
    // At least 1.5 px across so a star never falls between pixels.
    // (Not "half": a reserved word in GLSL ES 3.00.)
    float hs = clamp(r, 1.5, 40.0) * pixelRatio;
    vHalfSize = hs;
    gl_PointSize = 2.0 * hs + 1.0;
  }
`;

export const STAR_FRAGMENT = /* glsl */`
  uniform float pixelRatio;
  uniform float opacity;
  varying vec3 vColor;
  varying float vPeak;
  varying float vHalfSize;
  const float SIGMA = ${STAR_SIGMA.toFixed(3)};
  const float HALO_A = ${HALO_A.toFixed(4)};
  const float HALO_R = ${HALO_R.toFixed(3)};
  const float HALO_BETA = ${HALO_BETA.toFixed(3)};
  const float GAMMA = ${GAMMA.toFixed(3)};
  const float CEILING = ${CEILING.toFixed(2)};
  vec3 curve(vec3 v) {
    vec3 x = pow(max(v, vec3(0.0)), vec3(GAMMA));
    return CEILING * (vec3(1.0) - exp(-x / CEILING));
  }
  void main() {
    // Distance from the star in CSS pixels.
    vec2 p = (gl_PointCoord - 0.5) * (2.0 * vHalfSize + 1.0) / pixelRatio;
    float r2 = dot(p, p);
    // The sprite ends where the light reaches VISIBLE; the window takes the
    // last third down to zero so no edge (or the square) ever shows.
    float edge = 1.0 - smoothstep(0.66, 1.0, sqrt(r2) * pixelRatio / vHalfSize);
    float psf = (exp(-0.5 * r2 / (SIGMA * SIGMA)) + HALO_A * pow(1.0 + r2 / (HALO_R * HALO_R), -HALO_BETA)) * edge;
    // Each channel through the curve on its own, so a bright core saturates
    // to white the way a sensor's channels do.
    vec3 c = curve(vPeak * psf * vColor) * opacity;
    if (max(c.r, max(c.g, c.b)) < 0.002) discard;
    gl_FragColor = vec4(c, 1.0);
  }
`;

// Shared with the planets' points (src/sky/planetPoints.js), which are
// stars to the camera too.
export function createStarMaterial(renderer) {
  const material = new THREE.ShaderMaterial({
    uniforms: {
      pixelRatio: { value: renderer ? renderer.getPixelRatio() : 1 },
      exposure: { value: 1.0 },
      opacity: { value: 1.0 },
      uScintillation: { value: 0.0 },
      uTime: { value: 0 },
    },
    vertexShader: STAR_VERTEX,
    fragmentShader: STAR_FRAGMENT,
    transparent: true,
    depthWrite: false,
    // Depth testing on so planets, moons and ships hide the stars behind
    // them (with it off every planet looked transparent).
    depthTest: true,
    blending: THREE.AdditiveBlending,
  });
  // Marks the layer for anything that renders the sky into a cube (the
  // wormhole's lens, src/sky/skyCube.js) and scales pixelRatio for it.
  material.userData.starPoints = true;
  return material;
}

function starLayer(geometry, material, name, renderOrder) {
  const points = new THREE.Points(geometry, material);
  points.name = name;
  points.renderOrder = renderOrder;
  points.frustumCulled = false;
  return {
    mesh: points,
    count: geometry.drawRange.count === Infinity ? geometry.attributes.position.count : geometry.drawRange.count,
    setOpacity: (v) => { material.uniforms.opacity.value = v; },
    setExposure: (v) => { material.uniforms.exposure.value = v; },
    setScintillation: (v) => { material.uniforms.uScintillation.value = v; },
    setTime: (t) => { material.uniforms.uTime.value = t; },
    setPixelRatio: (v) => { material.uniforms.pixelRatio.value = v; },
    dispose: () => { geometry.dispose(); material.dispose(); },
  };
}

function buildGeometry(n, fill) {
  const positions = new Float32Array(n * 3), colors = new Float32Array(n * 3), flux = new Float32Array(n);
  fill(positions, colors, flux);
  return geometryOf(positions, colors, flux);
}

function geometryOf(positions, colors, flux) {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute('flux', new THREE.BufferAttribute(flux, 1));
  return geometry;
}

const _dir = new THREE.Vector3();

// HYG to magnitude 8: 16 bytes a star (unit vector, mag x 100, B-V x 100).
export async function createStarCatalog(renderer) {
  const res = await fetch(STAR_BIN_URL);
  if (!res.ok) throw new Error(`Failed to load ${STAR_BIN_URL}`);
  const view = new DataView(await res.arrayBuffer());
  const n = view.getUint32(0, true);
  const geometry = buildGeometry(n, (positions, colors, flux) => {
    for (let i = 0; i < n; i++) {
      const o = 4 + i * 16;
      equatorialToScene(view.getFloat32(o, true), view.getFloat32(o + 4, true), view.getFloat32(o + 8, true), _dir).multiplyScalar(STAR_SHELL_RADIUS);
      positions[i * 3] = _dir.x; positions[i * 3 + 1] = _dir.y; positions[i * 3 + 2] = _dir.z;
      unitLuminanceColour(view.getInt16(o + 14, true) / 100, colors, i * 3);
      flux[i] = fluxOf(view.getInt16(o + 12, true) / 100);
    }
  });
  // The bright stars draw over the faint ones.
  return starLayer(geometry, createStarMaterial(renderer), 'star-catalog', -999);
}

// Stars filled between two yields to the browser (about 5 ms of work).
const FAINT_SLICE = 60000;

// Tycho-2 from magnitude 8 to 10, brightest first: 10 bytes a star (RA and
// Dec as float32 radians, V x 20 as uint8, B-V x 50 as int8). maxMag trims
// it for lighter presets (the file is sorted, so a prefix is a magnitude cut).
export async function createFaintStarCatalog(renderer, { maxMag = 10 } = {}) {
  const res = await fetch(FAINT_BIN_URL);
  if (!res.ok) throw new Error(`Failed to load ${FAINT_BIN_URL}`);
  const view = new DataView(await gunzipIfNeeded(await res.arrayBuffer()));
  const total = view.getUint32(0, true);
  let n = 0;
  while (n < total && view.getUint8(4 + n * 10 + 8) / 20 <= maxMag) n++;
  const positions = new Float32Array(n * 3), colors = new Float32Array(n * 3), flux = new Float32Array(n);
  // Filled a slice at a time between frames: in one go it held the main
  // thread 35 ms, a dropped frame in whatever the visitor was doing.
  for (let start = 0; start < n; start += FAINT_SLICE) {
    if (start) await new Promise((resolve) => setTimeout(resolve, 0));
    for (let i = start, end = Math.min(n, start + FAINT_SLICE); i < end; i++) {
      const o = 4 + i * 10;
      const ra = view.getFloat32(o, true), dec = view.getFloat32(o + 4, true), cd = Math.cos(dec);
      equatorialToScene(cd * Math.cos(ra), cd * Math.sin(ra), Math.sin(dec), _dir).multiplyScalar(STAR_SHELL_RADIUS * 0.985);
      positions[i * 3] = _dir.x; positions[i * 3 + 1] = _dir.y; positions[i * 3 + 2] = _dir.z;
      unitLuminanceColour(view.getInt8(o + 9) / 50, colors, i * 3);
      flux[i] = fluxOf(view.getUint8(o + 8) / 20);
    }
  }
  return starLayer(geometryOf(positions, colors, flux), createStarMaterial(renderer), 'star-catalog-faint', -999.5);
}
